import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { PlayerRow, TournamentPlayers, WindowName } from '@/api/types';
import { dash, formatDateTime, formatDay, formatMoney, formatName } from '@/lib/format';
import { toPlayerId } from '@/lib/player-id';
import { Badge } from '@/ui/Badge';
import { Modal } from '@/ui/Modal';
import { Notice } from '@/ui/Notice';
import { SortableTh, Table, Td, Th } from '@/ui/Table';
import { useSort, type SortColumns } from '@/ui/use-sort';
import { PlayerDetail, PlayerDetailTitle } from './PlayerDetail';
import { PlayerFilterBar } from './PlayerFilterBar';
import { NO_FILTERS, filterOptions, filterPlayers } from './player-filters';
import { WINDOWS, windowHeading } from './windows';

type PlayerSortKey = 'links' | 'invited' | 'joined' | 'lastActive' | `${WindowName}-deposits` | `${WindowName}-ggr`;

const depositsIn = (window: WindowName) => (player: PlayerRow) => player.windows?.[window].depositEur;
const ggrIn = (window: WindowName) => (player: PlayerRow) => player.windows?.[window].ggrEur;

const SORT_COLUMNS: SortColumns<PlayerRow, PlayerSortKey> = {
  links: (player) => player.linksIssued,
  invited: (player) => player.invitedAt,
  joined: (player) => player.redeemedAt,
  lastActive: (player) => player.lastActiveDay,
  'before-deposits': depositsIn('before'),
  'before-ggr': ggrIn('before'),
  'during-deposits': depositsIn('during'),
  'during-ggr': ggrIn('during'),
  'after7-deposits': depositsIn('after7'),
  'after7-ggr': ggrIn('after7'),
  'after30-deposits': depositsIn('after30'),
  'after30-ggr': ggrIn('after30'),
};

const FIXED_COLUMNS = 12;

// The open player lives in the query string so the browser Back button closes the panel and an
// agent can hand a colleague a link straight to one player.
const PLAYER_PARAM = 'player';

export function PlayersTable({ data }: { data: TournamentPlayers }) {
  const [filters, setFilters] = useState(NO_FILTERS);
  const options = useMemo(() => filterOptions(data.players), [data.players]);
  const filtered = useMemo(() => filterPlayers(data.players, filters), [data.players, filters]);
  const { sorted: rows, sort, toggle } = useSort(filtered, SORT_COLUMNS, { key: 'invited', direction: 'desc' });

  const [params, setParams] = useSearchParams();
  const openedUuid = params.get(PLAYER_PARAM);
  // Looked up in every player, not the filtered rows: a linked player must open whatever the filter says.
  const opened = openedUuid ? data.players.find((player) => player.playerUuid === openedUuid) : undefined;

  function withPlayer(uuid: string | null): URLSearchParams {
    const next = new URLSearchParams(params);
    if (uuid === null) next.delete(PLAYER_PARAM);
    else next.set(PLAYER_PARAM, uuid);
    return next;
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <PlayerFilterBar filters={filters} options={options} windows={data.windows} onChange={setFilters} />
        <p className="py-2 text-sm text-slate-500">
          {rows.length} of {data.players.length} players
        </p>
      </div>
      <Table>
        <thead>
          <tr>
            <Th colSpan={FIXED_COLUMNS} />
            {WINDOWS.map((window) => (
              <Th
                key={window.key}
                colSpan={3}
                align="center"
                className={data.windows[window.key].partial ? 'text-amber-700' : ''}
              >
                {windowHeading(data.windows, window.key, window.label)}
              </Th>
            ))}
          </tr>
          <tr>
            <Th align="left">Player ID</Th>
            <Th align="left">Username</Th>
            <Th align="left">Name</Th>
            <Th align="left">Email</Th>
            <Th align="left">Country</Th>
            <Th align="left">Currency</Th>
            <Th align="left">Registered</Th>
            <Th align="left">Agent</Th>
            <SortableTh sortKey="links" sort={sort} onSort={toggle}>
              Links
            </SortableTh>
            <SortableTh align="left" sortKey="invited" sort={sort} onSort={toggle}>
              Invited
            </SortableTh>
            <SortableTh align="left" sortKey="joined" sort={sort} onSort={toggle}>
              Joined
            </SortableTh>
            <SortableTh align="left" sortKey="lastActive" sort={sort} onSort={toggle}>
              Last active
            </SortableTh>
            {WINDOWS.map((window) => [
              <Th key={`${window.key}-active`} align="center">
                Active
              </Th>,
              <SortableTh key={`${window.key}-eur`} sortKey={`${window.key}-deposits`} sort={sort} onSort={toggle}>
                Deposits €
              </SortableTh>,
              <SortableTh key={`${window.key}-ggr`} sortKey={`${window.key}-ggr`} sort={sort} onSort={toggle}>
                GGR €
              </SortableTh>,
            ])}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 && (
            <tr>
              <Td align="left" colSpan={FIXED_COLUMNS + WINDOWS.length * 3} className="text-slate-500">
                {data.players.length === 0
                  ? 'No links have been issued for this tournament.'
                  : 'No player matches the filters.'}
              </Td>
            </tr>
          )}

          {rows.map((player) => (
            <tr key={player.playerUuid}>
              <Td align="left" className="font-mono text-xs">
                <button
                  type="button"
                  onClick={() => setParams(withPlayer(player.playerUuid))}
                  className="font-mono text-blue-700 underline-offset-2 hover:underline"
                >
                  {toPlayerId(player.playerUuid)}
                </button>
              </Td>
              <Td align="left">{player.profile?.username ?? dash}</Td>
              <Td align="left">{formatName(player.profile)}</Td>
              <Td align="left">{player.profile?.email ?? dash}</Td>
              <Td align="left">{player.profile?.country ?? dash}</Td>
              <Td align="left">{player.profile?.currency ?? dash}</Td>
              <Td align="left">{formatDateTime(player.profile?.registrationDate)}</Td>
              <Td align="left">{player.agentIds.join(', ')}</Td>
              <Td>{player.linksIssued}</Td>
              <Td align="left">{formatDateTime(player.invitedAt)}</Td>
              <Td align="left">
                {formatDateTime(player.redeemedAt)} {player.joinedViaOpenLink && <Badge value="open link" />}
              </Td>
              <Td align="left">{formatDay(player.lastActiveDay)}</Td>
              {WINDOWS.map((window) => {
                const metrics = player.windows?.[window.key];
                return [
                  <Td key={`${window.key}-active`} align="center">
                    {metrics ? (metrics.active ? 'yes' : 'no') : dash}
                  </Td>,
                  <Td key={`${window.key}-eur`}>{metrics ? formatMoney(metrics.depositEur) : dash}</Td>,
                  <Td key={`${window.key}-ggr`}>{metrics ? formatMoney(metrics.ggrEur) : dash}</Td>,
                ];
              })}
            </tr>
          ))}
        </tbody>
      </Table>
      {openedUuid && (
        <Modal
          title={<PlayerDetailTitle playerUuid={openedUuid} isTest={opened?.isTest} />}
          onClose={() => setParams(withPlayer(null), { replace: true })}
        >
          {opened ? (
            <PlayerDetail player={opened} windows={data.windows} />
          ) : (
            <Notice>No player with that id received a link for this tournament.</Notice>
          )}
        </Modal>
      )}
    </div>
  );
}
